import React, { useEffect, useRef, useState } from "react";
import { Alert, Form, Spinner } from "react-bootstrap";
import { FiArrowLeft, FiCalendar, FiCheck, FiChevronDown, FiFileText, FiImage, FiPlus, FiRotateCcw, FiSave, FiSearch, FiSettings, FiTrash2, FiUsers, FiX } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";

import { getApiUrl } from "../../utils/apiUrl";
import { getCsrfToken } from "../../utils/getCsrfToken";
import "./IniciativaFormPage.css";

const emptyForm = {
  tipo_id: "", nombre: "", etiqueta: "", oficio_ids: [], comuna: "", formato: "",
  lugar: "", precio: "", precio_preferencial: "", cupos: "", relatora_ids: [],
  f_inicio: "", f_termino: "", hora_inicio: "", hora_termino: "", dias_semana: [],
  descripcion: "", contenidos_items: [""], comida: "", requisitos_items: [""],
  horas_c: "", horas_t: "", horas_e: "", equipo: [],
  empresa_ids: [], otec_ids: [],
  asociado_ids: [],
};

const weekdays = [
  ["lunes", "Lun"], ["martes", "Mar"], ["miercoles", "Mié"],
  ["jueves", "Jue"], ["viernes", "Vie"], ["sabado", "Sáb"],
  ["domingo", "Dom"],
];

const isoDate = (value) => {
  if (!value) return "";
  const parts = value.split("-");
  return parts[0].length === 4 ? value : parts.reverse().join("-");
};

const parseRequirements = (value) => {
  const items = (value || "")
    .split(/\r?\n/)
    .map((item) => item.replace(/^\s*[•*-]\s*/, "").trim())
    .filter(Boolean);
  return items.length ? items : [""];
};

const normalizeSearch = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLocaleLowerCase("es");

const MultiSelect = ({ label, value, options, onChange, help }) => {
  const [query, setQuery] = useState("");
  const selectedValues = new Set(value.map(String));
  const optionValue = (option) => String(option.id ?? option.rut);
  const normalizedQuery = normalizeSearch(query.trim());
  const selectedOptions = options.filter((option) => selectedValues.has(optionValue(option)));
  const filteredOptions = normalizedQuery
    ? options.filter((option) =>
        normalizeSearch(`${option.nombre} ${option.rut || ""}`).includes(normalizedQuery),
      )
    : [];
  const visibleOptions = filteredOptions.slice(0, 20);

  const toggle = (rawValue) => {
    const selectedValue = String(rawValue);
    onChange(
      selectedValues.has(selectedValue)
        ? value.filter((item) => String(item) !== selectedValue)
        : [...value, selectedValue],
    );
    setQuery("");
  };

  return (
    <Form.Group className="initiative-picker field-optional">
      <div className="initiative-picker-label-row">
        <Form.Label>{label}</Form.Label>
        <span>{value.length} {value.length === 1 ? "selección" : "selecciones"}</span>
      </div>
      <div className="initiative-picker-shell">
        {selectedOptions.length > 0 && (
          <div className="initiative-picker-chips">
            {selectedOptions.map((option) => (
              <button key={optionValue(option)} type="button" className="initiative-picker-chip" onClick={() => toggle(optionValue(option))} title={`Quitar ${option.nombre}`}>
                <span>{option.nombre}</span><FiX />
              </button>
            ))}
          </div>
        )}
        <label className="initiative-picker-search">
          <FiSearch />
          <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Escribe para buscar..." aria-label={`Buscar en ${label}`} />
        </label>
        {normalizedQuery && <div className="initiative-picker-options">
          {visibleOptions.map((option) => {
            const itemValue = optionValue(option);
            const selected = selectedValues.has(itemValue);
            return (
              <label key={itemValue} className={`initiative-picker-option${selected ? " selected" : ""}`}>
                <input type="checkbox" checked={selected} onChange={() => toggle(itemValue)} />
                <span className="initiative-picker-check"><FiCheck /></span>
                <span className="initiative-picker-option-copy"><strong>{option.nombre}{option.activa === false ? " (inactiva)" : ""}</strong>{option.rut && <small>{option.rut}</small>}</span>
              </label>
            );
          })}
          {filteredOptions.length === 0 && <p className="initiative-picker-empty">No se encontraron resultados.</p>}
          {filteredOptions.length > visibleOptions.length && <p className="initiative-picker-empty">Hay más resultados. Escribe más para acotar la lista.</p>}
        </div>}
      </div>
      {help && <Form.Text>{help}</Form.Text>}
    </Form.Group>
  );
};

const SearchSelect = ({ label, value, options, onChange, help }) => {
  const [query, setQuery] = useState("");
  const selected = options.find((option) => String(option.value) === String(value));
  const normalizedQuery = normalizeSearch(query.trim());
  const filteredOptions = normalizedQuery
    ? options.filter((option) => normalizeSearch(option.label).includes(normalizedQuery))
    : [];
  const visibleOptions = filteredOptions.slice(0, 20);

  const select = (optionValue) => {
    onChange(optionValue);
    setQuery("");
  };

  return (
    <Form.Group className="initiative-picker field-optional">
      <div className="initiative-picker-label-row">
        <Form.Label>{label}</Form.Label>
        <span>{selected ? "Seleccionada" : "Sin selección"}</span>
      </div>
      <div className="initiative-picker-shell">
        {selected && (
          <div className="initiative-picker-chips">
            <button type="button" className="initiative-picker-chip" onClick={() => onChange("")} title={`Quitar ${selected.label}`}>
              <span>{selected.label}</span><FiX />
            </button>
          </div>
        )}
        <label className="initiative-picker-search">
          <FiSearch />
          <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar comuna..." aria-label={`Buscar en ${label}`} />
        </label>
        {normalizedQuery && <div className="initiative-picker-options">
          {visibleOptions.map((option) => {
            const isSelected = String(option.value) === String(value);
            return (
              <label key={option.value} className={`initiative-picker-option${isSelected ? " selected" : ""}`}>
                <input type="radio" name="initiative-comuna" checked={isSelected} onChange={() => select(option.value)} />
                <span className="initiative-picker-check"><FiCheck /></span>
                <span className="initiative-picker-option-copy"><strong>{option.label}</strong></span>
              </label>
            );
          })}
          {filteredOptions.length === 0 && <p className="initiative-picker-empty">No se encontraron comunas.</p>}
          {filteredOptions.length > visibleOptions.length && <p className="initiative-picker-empty">Hay más resultados. Escribe más para acotar la lista.</p>}
        </div>}
      </div>
      {help && <Form.Text>{help}</Form.Text>}
    </Form.Group>
  );
};

const BadgeMultiSelect = ({ label, value, options, onChange, help }) => {
  const selectedValues = new Set(value.map(String));
  const toggle = (optionId) => {
    const selectedValue = String(optionId);
    onChange(
      selectedValues.has(selectedValue)
        ? value.filter((item) => String(item) !== selectedValue)
        : [...value, selectedValue],
    );
  };

  return (
    <Form.Group className="initiative-form-full field-optional initiative-badge-field">
      <div className="initiative-picker-label-row">
        <Form.Label>{label}</Form.Label>
        <span>{value.length} {value.length === 1 ? "selección" : "selecciones"}</span>
      </div>
      <div className="initiative-option-badges" role="group" aria-label={label}>
        {options.map((option) => {
          const selected = selectedValues.has(String(option.id));
          return (
            <button
              key={option.id}
              type="button"
              className={selected ? "selected" : ""}
              aria-pressed={selected}
              onClick={() => toggle(option.id)}
            >
              {option.nombre}
            </button>
          );
        })}
      </div>
      {help && <Form.Text>{help}</Form.Text>}
    </Form.Group>
  );
};

const InitiativeSection = ({ icon: Icon, title, description, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <details className="initiative-form-section" open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary className="initiative-section-toggle">
        <span className="initiative-section-icon"><Icon /></span>
        <span className="initiative-section-copy"><strong>{title}</strong><small>{description}</small></span>
        <FiChevronDown className="initiative-section-chevron" aria-hidden="true" />
      </summary>
      <div className="initiative-form-grid">{children}</div>
    </details>
  );
};

const IniciativaFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const [form, setForm] = useState(emptyForm);
  const [options, setOptions] = useState({ productos: [], formatos: [], oficios: [], comunas: [], empresas: [], profesionales: [], personas: [] });
  const [image, setImage] = useState(null);
  const [existingImage, setExistingImage] = useState("");
  const [removeImage, setRemoveImage] = useState(false);
  const imageInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peopleError, setPeopleError] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const requests = [fetch(getApiUrl("api/iniciativas-opciones/?only=base"), { credentials: "include" }).then((response) => {
      if (!response.ok) throw new Error();
      return response.json();
    })];
    if (editing) requests.push(fetch(getApiUrl(`api/iniciativas/${id}/?form=true`), { credentials: "include" }).then((response) => {
      if (!response.ok) throw new Error();
      return response.json();
    }));
    Promise.all(requests)
      .then(([available, initiative]) => {
        if (!active) return;
        setOptions((current) => ({ ...current, ...available, personas: [] }));
        if (initiative) {
          setExistingImage(initiative.imagen || "");
          setForm({
            ...emptyForm, ...initiative,
            tipo_id: String(initiative.tipo?.id || ""),
            oficio_ids: (initiative.oficios || []).map((item) => String(item.id)),
            f_inicio: isoDate(initiative.f_inicio), f_termino: isoDate(initiative.f_termino),
            contenidos_items: parseRequirements(initiative.contenidos),
            requisitos_items: parseRequirements(initiative.requisitos),
            equipo: (initiative.equipo || []).map((item) => ({ profesional_id: String(item.profesional.id), rol: item.rol })),
            relatora_ids: (initiative.relatoras || []).map((item) => String(item.id)),
            empresa_ids: initiative.empresa.map((item) => String(item.id)), otec_ids: initiative.otec.map((item) => String(item.id)), asociado_ids: initiative.asociado.map((item) => String(item.id)),
          });
        } else {
          const requestedType = new URLSearchParams(window.location.search).get("tipo");
          const requestedProduct = available.productos?.find(
            (item) => item.nombre.toLocaleLowerCase("es") === requestedType?.toLocaleLowerCase("es"),
          );
          if (requestedProduct) {
            setForm((current) => ({ ...current, tipo_id: String(requestedProduct.id) }));
          }
        }
      })
      .catch(() => setError("No se pudo cargar el formulario."))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [editing, id]);

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateRequirement = (index, value) => setForm((current) => ({
    ...current,
    requisitos_items: current.requisitos_items.map((item, itemIndex) => itemIndex === index ? value : item),
  }));
  const addRequirement = () => setForm((current) => ({
    ...current, requisitos_items: [...current.requisitos_items, ""],
  }));
  const removeRequirement = (index) => setForm((current) => ({
    ...current,
    requisitos_items: current.requisitos_items.length === 1
      ? [""]
      : current.requisitos_items.filter((_item, itemIndex) => itemIndex !== index),
  }));
  const updateContent = (index, value) => setForm((current) => ({
    ...current,
    contenidos_items: current.contenidos_items.map((item, itemIndex) => itemIndex === index ? value : item),
  }));
  const addContent = () => setForm((current) => ({
    ...current, contenidos_items: [...current.contenidos_items, ""],
  }));
  const removeContent = (index) => setForm((current) => ({
    ...current,
    contenidos_items: current.contenidos_items.length === 1
      ? [""]
      : current.contenidos_items.filter((_item, itemIndex) => itemIndex !== index),
  }));
  const addTeamMember = () => setForm((current) => ({
    ...current, equipo: [...current.equipo, { profesional_id: "", rol: "" }],
  }));
  const updateTeamMember = (index, field, value) => setForm((current) => ({
    ...current,
    equipo: current.equipo.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item),
  }));
  const removeTeamMember = (index) => setForm((current) => ({
    ...current, equipo: current.equipo.filter((_item, itemIndex) => itemIndex !== index),
  }));
  const toggleWeekday = (weekday) => setForm((current) => ({
    ...current,
    dias_semana: current.dias_semana.includes(weekday)
      ? current.dias_semana.filter((item) => item !== weekday)
      : [...current.dias_semana, weekday],
  }));
  const listOptions = (items) => items.map((item) => ({ value: item.value, label: item.label }));
  const selectedProduct = options.productos.find((item) => String(item.id) === String(form.tipo_id));
  const isProgram = selectedProduct?.nombre?.toLocaleLowerCase("es") === "programa";

  useEffect(() => {
    let active = true;
    if (loading || options.personas.length) return () => { active = false; };
    setPeopleLoading(true);
    setPeopleError("");
    fetch(getApiUrl("api/iniciativas-opciones/?only=personas"), { credentials: "include" })
      .then((response) => {
        if (!response.ok) throw new Error("No se pudieron cargar las Personas disponibles.");
        return response.json();
      })
      .then((data) => {
        if (active) setOptions((current) => ({ ...current, personas: data.personas || [] }));
      })
      .catch((requestError) => { if (active) setPeopleError(requestError.message); })
      .finally(() => { if (active) setPeopleLoading(false); });
    return () => { active = false; };
  }, [loading, options.personas.length]);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    const payload = { ...form };
    delete payload.participaciones;
    payload.contenidos = form.contenidos_items.map((item) => item.trim()).filter(Boolean).join("\n");
    payload.requisitos = form.requisitos_items.map((item) => item.trim()).filter(Boolean).join("\n");
    ["imagen", "tipo", "oficio", "oficios", "relatoras", "contenidos_items", "requisitos_items", "jefe", "coordinador", "psicologa", "url_inscripcion", "url_inscripcion_origen", "origen_tipo", "origen_id", "categoria_legacy", "created_at", "updated_at", "proceso_origen", "proceso_seleccion", "inscripcion_habilitada", "activa", "mostrar_en_calendario", "usar_imagen_estandar"].forEach((field) => delete payload[field]);
    payload.oficio_ids = form.oficio_ids.map(Number);
    payload.relatora_ids = form.relatora_ids.map(Number);
    payload.precio = form.precio === "" || form.precio === null ? null : Number(form.precio);
    payload.precio_preferencial = form.precio_preferencial === "" || form.precio_preferencial === null ? null : Number(form.precio_preferencial);
    payload.cupos = form.cupos === "" || form.cupos === null ? null : Number(form.cupos);
    ["horas_c", "horas_t", "horas_e"].forEach((field) => { payload[field] = payload[field] === "" ? null : Number(payload[field]); });
    payload.tipo_id = Number(payload.tipo_id);
    payload.remove_imagen = editing && removeImage;
    ["hora_inicio", "hora_termino"].forEach((field) => { payload[field] = payload[field] || null; });
    const body = new FormData();
    body.append("payload", JSON.stringify(payload));
    if (image) body.append("imagen", image);
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(`api/iniciativas/${editing ? `${id}/` : ""}`), { method: editing ? "PATCH" : "POST", credentials: "include", headers: { "X-CSRFToken": csrfToken }, body });
      const data = await response.json();
      if (!response.ok) {
        const message = Object.entries(data).map(([field, detail]) => `${field}: ${Array.isArray(detail) ? detail.join(" ") : detail}`).join(" · ");
        throw new Error(message || "No se pudo guardar la iniciativa.");
      }
      const savedType = data.tipo?.nombre?.toLocaleLowerCase("es");
      if (!data.activa || savedType === "suspendida") {
        navigate(savedType === "programa" ? "/programas" : "/iniciativas");
      } else {
        const destination = savedType === "programa" ? "/programas" : "/iniciativas";
        navigate(`${destination}/${data.id}`);
      }
    } catch (requestError) {
      setError(requestError.message);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="initiative-form-loading"><Spinner animation="border" /><span>Cargando formulario...</span></div>;

  return (
    <div className="initiative-form-page">
      <div className="initiative-form-heading"><button type="button" onClick={() => navigate(isProgram ? "/programas" : "/iniciativas")}><FiArrowLeft /></button><div><span>{isProgram ? "Programas" : "Iniciativas"}</span><h1>{editing ? "Editar" : "Crear"} {isProgram ? "programa" : "iniciativa"}</h1><p>Completa la información operativa y sus relaciones principales.</p></div></div>
      {error && <Alert variant="danger">{error}</Alert>}
      <div className="initiative-field-legend" aria-label="Código de color de campos"><span className="required">Obligatorio</span><span className="optional">Opcional</span></div>
      <form onSubmit={submit}>
        <InitiativeSection icon={FiSettings} title="Información principal" description="Identidad y clasificación de la iniciativa." defaultOpen>
          <Form.Group className="initiative-form-full field-required"><Form.Label>Título</Form.Label><Form.Control required value={form.nombre} onChange={(event) => update("nombre", event.target.value)} placeholder={isProgram ? "Título del programa" : "Título de la iniciativa"} /></Form.Group>
          <div className="initiative-inline-fields initiative-form-full">
            <Form.Group className="field-required"><Form.Label>Clasificación</Form.Label><Form.Select required value={form.tipo_id} onChange={(event) => update("tipo_id", event.target.value)}><option value="">Seleccionar</option>{options.productos.map((item) => <option key={item.id} value={item.id}>{item.nombre}{item.activo ? "" : " (inactivo)"}</option>)}</Form.Select><Form.Text>Programa crea un proceso de selección. Las demás opciones solo ayudan a ordenar las iniciativas.</Form.Text></Form.Group>
            <Form.Group className="field-optional"><Form.Label>Etiqueta/Código (Gestión interna)</Form.Label><Form.Control value={form.etiqueta || ""} onChange={(event) => update("etiqueta", event.target.value)} /></Form.Group>
          </div>
          <BadgeMultiSelect label="Oficios" value={form.oficio_ids} options={options.oficios} onChange={(value) => update("oficio_ids", value)} help="Selecciona uno o más oficios asociados." />
          <div className="initiative-inline-fields initiative-form-full">
            <SearchSelect label="Comuna" value={form.comuna || ""} options={listOptions(options.comunas)} onChange={(value) => update("comuna", value)} help="Selección única. Escribe parte del nombre para encontrarla rápidamente." />
            <Form.Group className="field-optional"><Form.Label>Lugar</Form.Label><Form.Control value={form.lugar || ""} onChange={(event) => update("lugar", event.target.value)} placeholder="Dirección, sede o lugar de encuentro" /></Form.Group>
          </div>
          <Form.Group className="initiative-form-full field-optional"><Form.Label>Formato</Form.Label><Form.Select value={form.formato || ""} onChange={(event) => update("formato", event.target.value)}><option value="">Seleccionar</option>{listOptions(options.formatos).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</Form.Select></Form.Group>
          <Form.Group className="initiative-form-full field-optional"><Form.Label>Precio</Form.Label><div className="initiative-price-input"><span>$</span><Form.Control type="number" min="0" step="1" value={form.precio ?? ""} onChange={(event) => update("precio", event.target.value)} placeholder="0" /></div><Form.Text>Usa 0 cuando sea gratuito.</Form.Text></Form.Group>
          <Form.Group className="initiative-form-full field-optional"><Form.Label>Precio preferencial</Form.Label><div className="initiative-price-input"><span>$</span><Form.Control type="number" min="0" step="1" value={form.precio_preferencial ?? ""} onChange={(event) => update("precio_preferencial", event.target.value)} placeholder="0" /></div><Form.Text>Déjalo vacío cuando no exista un valor preferencial.</Form.Text></Form.Group>
          <Form.Group className="initiative-form-full field-optional"><Form.Label>Cupos</Form.Label><Form.Control type="number" min="1" step="1" value={form.cupos ?? ""} onChange={(event) => update("cupos", event.target.value)} placeholder="Cantidad referencial" /><Form.Text>Este número no limita las inscripciones ni elimina participantes.</Form.Text></Form.Group>
          <Form.Group className="initiative-form-full field-optional"><Form.Label>Coffee break</Form.Label><Form.Select value={form.comida || ""} onChange={(event) => update("comida", event.target.value)}><option value="">Sin respuesta</option><option value="si">Sí</option><option value="no">No</option></Form.Select></Form.Group>
        </InitiativeSection>

        <InitiativeSection icon={FiFileText} title="Descripción y contenidos" description="Presentación breve y materias que abordará la iniciativa.">
          <Form.Group className="initiative-form-full field-optional"><Form.Label>Descripción breve</Form.Label><Form.Control as="textarea" rows={4} value={form.descripcion || ""} onChange={(event) => update("descripcion", event.target.value)} placeholder="Resume el propósito y alcance de la iniciativa" /></Form.Group>
          <Form.Group className="initiative-form-full field-optional">
            <Form.Label>Tabla de contenidos</Form.Label>
            <div className="initiative-requirements-editor">
              {form.contenidos_items.map((content, index) => (
                <div className="initiative-requirement-row" key={index}>
                  <span aria-hidden="true">•</span>
                  <Form.Control value={content} onChange={(event) => updateContent(index, event.target.value)} placeholder="Escribe un contenido" />
                  <button type="button" onClick={() => removeContent(index)} aria-label={`Eliminar contenido ${index + 1}`}><FiTrash2 /></button>
                </div>
              ))}
              <button className="initiative-add-row" type="button" onClick={addContent}><FiPlus /> Agregar contenido</button>
            </div>
          </Form.Group>
        </InitiativeSection>

        <InitiativeSection icon={FiCalendar} title="Fechas y horario" description="Planificación, días de funcionamiento y carga horaria.">
          <Form.Group className="field-optional"><Form.Label>Fecha de inicio</Form.Label><Form.Control type="date" value={form.f_inicio || ""} onChange={(event) => update("f_inicio", event.target.value)} /></Form.Group>
          <Form.Group className="field-optional"><Form.Label>Fecha de término</Form.Label><Form.Control type="date" min={form.f_inicio || undefined} value={form.f_termino || ""} onChange={(event) => update("f_termino", event.target.value)} /></Form.Group>
          <Form.Group className="field-optional"><Form.Label>Hora de inicio</Form.Label><Form.Control type="time" value={form.hora_inicio || ""} onChange={(event) => update("hora_inicio", event.target.value)} /></Form.Group>
          <Form.Group className="field-optional"><Form.Label>Hora de término</Form.Label><Form.Control type="time" min={form.hora_inicio || undefined} value={form.hora_termino || ""} onChange={(event) => update("hora_termino", event.target.value)} /></Form.Group>
          {[["horas_c", "Horas de capacitación"], ["horas_t", "Horas de talleres"], ["horas_e", "Horas extras"]].map(([field, label]) => <Form.Group className="field-optional" key={field}><Form.Label>{label}</Form.Label><Form.Control type="number" min="0" value={form[field] ?? ""} onChange={(event) => update(field, event.target.value)} /></Form.Group>)}
          <Form.Group className="initiative-form-full field-optional">
            <Form.Label>Días en que se realiza</Form.Label>
            <div className="initiative-weekdays" role="group" aria-label="Días en que se realiza la iniciativa">
              {weekdays.map(([value, label]) => (
                <button key={value} type="button" className={form.dias_semana.includes(value) ? "active" : ""} aria-pressed={form.dias_semana.includes(value)} onClick={() => toggleWeekday(value)}>{label}</button>
              ))}
            </div>
            <Form.Text>Marca solamente los días en que la iniciativa puede realizarse.</Form.Text>
          </Form.Group>
          <Form.Group className="initiative-form-full field-optional">
            <Form.Label>Requisitos de participación</Form.Label>
            <div className="initiative-requirements-editor">
              {form.requisitos_items.map((requirement, index) => (
                <div className="initiative-requirement-row" key={index}>
                  <span aria-hidden="true">•</span>
                  <Form.Control value={requirement} onChange={(event) => updateRequirement(index, event.target.value)} placeholder="Escribe un requisito" />
                  <button type="button" onClick={() => removeRequirement(index)} aria-label={`Eliminar requisito ${index + 1}`}><FiTrash2 /></button>
                </div>
              ))}
              <button className="initiative-add-row" type="button" onClick={addRequirement}><FiPlus /> Agregar requisito</button>
            </div>
          </Form.Group>
        </InitiativeSection>

        <InitiativeSection icon={FiUsers} title="Equipo y organizaciones" description="Responsables y entidades vinculadas.">
          <div className="initiative-form-subsection initiative-form-full">
            <div className="initiative-subsection-heading"><strong>Equipo responsable</strong><span>Profesionales a cargo y sus responsabilidades.</span></div>
            <Form.Group className="field-optional">
              <div className="initiative-team-editor">
                {form.equipo.length === 0 && <p>No hay profesionales asignados.</p>}
                {form.equipo.map((member, index) => (
                  <div className="initiative-team-row" key={index}>
                    <Form.Select required value={member.profesional_id} onChange={(event) => updateTeamMember(index, "profesional_id", event.target.value)} aria-label={`Profesional ${index + 1}`}>
                      <option value="">Seleccionar profesional</option>
                      {options.profesionales.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}
                    </Form.Select>
                    <Form.Control required value={member.rol} onChange={(event) => updateTeamMember(index, "rol", event.target.value)} placeholder="Rol o responsabilidad" aria-label={`Rol del profesional ${index + 1}`} />
                    <button type="button" onClick={() => removeTeamMember(index)} aria-label={`Eliminar profesional ${index + 1}`}><FiTrash2 /></button>
                  </div>
                ))}
                <button className="initiative-add-row" type="button" onClick={addTeamMember}><FiPlus /> Agregar profesional</button>
              </div>
            </Form.Group>
          </div>

          <div className="initiative-form-subsection initiative-form-full">
            <div className="initiative-subsection-heading"><strong>Relatoras</strong><span>Personas responsables de impartir contenidos.</span></div>
            {peopleLoading ? <div className="initiative-people-loading"><Spinner animation="border" size="sm" /><span>Cargando Relatoras disponibles...</span></div> : peopleError ? <Alert variant="danger" className="mb-0">{peopleError}</Alert> : <MultiSelect label="Relatora(s)" value={form.relatora_ids} options={options.personas} onChange={(value) => update("relatora_ids", value)} help="Escribe un nombre o RUT para buscar." />}
          </div>

          <div className="initiative-form-subsection initiative-form-full">
            <div className="initiative-subsection-heading"><strong>Organizaciones</strong><span>Entidades principales, OTEC y colaboradoras.</span></div>
            <div className="initiative-subsection-grid">
              <MultiSelect label="Organizaciones principales" value={form.empresa_ids} options={options.empresas} onChange={(value) => update("empresa_ids", value)} help="Escribe para buscar y seleccionar." />
              <MultiSelect label="OTEC" value={form.otec_ids} options={options.empresas} onChange={(value) => update("otec_ids", value)} help="Escribe para buscar y seleccionar." />
              <MultiSelect label="Organizaciones asociadas" value={form.asociado_ids} options={options.empresas} onChange={(value) => update("asociado_ids", value)} help="Escribe para buscar y seleccionar." />
            </div>
          </div>
        </InitiativeSection>

        <InitiativeSection icon={FiImage} title="Presentación" description="Imagen utilizada para presentar la iniciativa.">
          <Form.Group className="initiative-form-full field-optional"><Form.Label>Imagen</Form.Label><Form.Control ref={imageInputRef} type="file" accept="image/png,image/jpeg,image/gif" onChange={(event) => { setImage(event.target.files[0] || null); setRemoveImage(false); }} />{image ? <div className="initiative-current-media"><FiImage /><span><strong>Nueva imagen</strong><small>{image.name}</small></span><button type="button" onClick={() => { setImage(null); if (imageInputRef.current) imageInputRef.current.value = ""; }}><FiTrash2 /> Descartar</button></div> : existingImage ? <div className={`initiative-current-media${removeImage ? " is-removed" : ""}`}>{!removeImage && <img className="initiative-form-preview" src={existingImage} alt="Imagen actual" />}<span><strong>{removeImage ? "La imagen se quitará al guardar" : "Imagen actual"}</strong><small>{removeImage ? "Puedes deshacer esta decisión." : "Se conservará si no haces cambios."}</small></span><button type="button" onClick={() => setRemoveImage((current) => !current)}>{removeImage ? <><FiRotateCcw /> Deshacer</> : <><FiTrash2 /> Quitar</>}</button></div> : <Form.Text>Opcional</Form.Text>}</Form.Group>
        </InitiativeSection>

        <div className="initiative-form-actions"><button type="button" onClick={() => navigate(isProgram ? "/programas" : "/iniciativas")}>Cancelar</button><button className="primary" disabled={saving}><FiSave /> {saving ? "Guardando..." : `Guardar ${isProgram ? "programa" : "iniciativa"}`}</button></div>
      </form>
    </div>
  );
};

export default IniciativaFormPage;
